// components/InstallPrompt.js
import React, { useEffect, useState } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  IconButton,
  Alert,
  Collapse,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent.toLowerCase());
const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent.toLowerCase());
const DISMISS_STORAGE_KEY = "installPromptDismissed";

const INSTALL_TEXT = {
  title: "Add to your Home Screen",
  buttonAdd: "Add now",
  buttonGotIt: "Got it",
  alertTitle: "Add to your Home Screen",
  androidDescription: "Add it to your home screen for better experience.",
  iosStepsTitle: "For a better experience:",
  iosStep1: "1. Tap the Share icon in Safari",
  iosStep2: "2. Scroll down and tap “Add to Home Screen”",
};

const modalStyle = {
  position: "fixed",
  top: "60%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: 1,
  p: 3,
  width: "90%",
  maxWidth: 400,
  zIndex: 999,
  outline: "none",
};

const InstallPromptContent = () => {
  if (isIOS && isSafari) {
    return (
      <Box>
        <Typography variant="body2" mb={1}>
          {INSTALL_TEXT.iosStepsTitle}
        </Typography>
        <Typography variant="body2">{INSTALL_TEXT.iosStep1}</Typography>
        <Typography variant="body2" mb={2}>
          {INSTALL_TEXT.iosStep2}
        </Typography>
      </Box>
    );
  }
  return (
    <Typography variant="body2" mb={2}>
      {INSTALL_TEXT.androidDescription}
    </Typography>
  );
};

const InstallPrompt = ({ showPrompt, triggerPrompt, mode = "modal" }) => {
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(() => {
    try {
      return window.localStorage.getItem(DISMISS_STORAGE_KEY) !== "1";
    } catch {
      return true;
    }
  });

  const dismissPrompt = () => {
    try {
      window.localStorage.setItem(DISMISS_STORAGE_KEY, "1");
    } catch (err) {
      console.warn("Failed to persist install prompt dismissal:", err);
    }
    setVisible(false);
    setOpen(false);
  };

  useEffect(() => {
    if (mode === "modal" && showPrompt) setOpen(true);
  }, [showPrompt, mode]);

  // ---------- Banner (fixed gutters & spacing) ----------
  if (mode === "banner") {
    if (!showPrompt || !visible) return null;

    return (
      <Collapse in={visible}>
        <Alert
          severity="info"
          icon={false} // disables the left icon slot (removes that huge left gutter)
          sx={{
            borderRadius: "10px",
            mb: 1,
            p: 0, // we'll control inner spacing ourselves
            "& .MuiAlert-message": {
              width: "100%",
              p: 0,
              m: 0,
            },
          }}
        >
          <Box sx={{ p: 2 }}>
            {/* Close button on its own row (top-right), compact */}
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <IconButton
                aria-label="Close"
                size="small"
                onClick={dismissPrompt}
                sx={{ p: 0.5 }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Title row (full width) */}
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
              {INSTALL_TEXT.alertTitle}
            </Typography>

            {/* Description row (full width) */}
            <InstallPromptContent />

            {/* Button row (full width) */}
            {!isIOS && (
              <Button
                variant="contained"
                onClick={triggerPrompt}
                fullWidth
                sx={(theme) => ({
                  mt: 1,
                  backgroundColor: theme.palette.primary.main,
                })}
              >
                {INSTALL_TEXT.buttonAdd}
              </Button>
            )}
          </Box>
        </Alert>
      </Collapse>
    );
  }

  // ---------- Modal (unchanged aside from palette fix) ----------
  return (
    <Modal open={open && visible} onClose={dismissPrompt}>
      <Box sx={modalStyle}>
        <IconButton
          onClick={dismissPrompt}
          sx={{ position: "absolute", top: 10, right: 10 }}
          aria-label="Close"
        >
          <CloseIcon />
        </IconButton>

        <Typography variant="h6" gutterBottom>
          {INSTALL_TEXT.title}
        </Typography>

        <InstallPromptContent />

        {!isIOS && (
          <Button
            variant="contained"
            fullWidth
            onClick={() => {
              triggerPrompt();
              setOpen(false);
            }}
            sx={(theme) => ({
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.common.white,
              py: 1.25,
            })}
          >
            {INSTALL_TEXT.buttonAdd}
          </Button>
        )}

        {isIOS && isSafari && (
          <Button
            variant="outlined"
            fullWidth
            onClick={dismissPrompt}
            sx={{
              mt: 1,
              py: 1.1,
            }}
          >
            {INSTALL_TEXT.buttonGotIt}
          </Button>
        )}
      </Box>
    </Modal>
  );
};

export default InstallPrompt;
